/** THE operator-resolution rules for the QuerySearch DSL — the single place
 * that knows which concrete operator a namespace's `=` / `=~` maps to.
 *
 * Used by the parser (typed sigils), the input widget (chip building) and —
 * via `resolveConditionOperator` — every programmatic condition builder
 * (grid cell "filter by this …" actions). Keeping them on one code path is
 * what guarantees a condition can never carry an operator its namespace
 * doesn't declare, no matter where it was constructed.
 */
import type { QueryOperator } from './types';

/** The equality operator the `=` (or `:`) sigil resolves to: `iexact` when
 *  declared, else `exact`, else null (the namespace has no equality match). */
export function equalityOperator(
  ops: readonly QueryOperator[],
): 'iexact' | 'exact' | null {
  if (ops.includes('iexact')) return 'iexact';
  if (ops.includes('exact')) return 'exact';
  return null;
}

/** Resolve a typed sigil against a namespace's declared operators.
 *  Null = the namespace doesn't support that match kind. */
export function sigilOperator(
  sigil: string,
  ops: readonly QueryOperator[],
): QueryOperator | 'idmatch' | null {
  if (sigil === '=~') return ops.includes('icontains') ? 'icontains' : null;
  // `=#` is match-by-id → the FE-only node operator `idmatch` (serialised as `=#`,
  // mapped to the wire operator `exact` in toWireAst). It's offered only where
  // `exact` accompanies `iexact` (tag/tour/category/user) — for value-type
  // namespaces (date/pax/…) `exact` is an ordinary equality op reached via `=`.
  if (sigil === '=#') return ops.includes('exact') && ops.includes('iexact') ? 'idmatch' : null;
  return equalityOperator(ops);
}

/** Operator for a programmatically built condition: the preferred one when
 *  the namespace declares it, otherwise the namespace's equality operator.
 *  Cell actions MUST route through this — a hardcoded operator the config
 *  (or the backend registry behind it) doesn't declare would 400 at runtime. */
export function resolveConditionOperator(
  ops: readonly QueryOperator[] | undefined,
  preferred: QueryOperator,
): QueryOperator | null {
  if (!ops) return null;
  if (ops.includes(preferred)) return preferred;
  // A range carries a [from, to] value — no scalar operator can take it.
  if (preferred === 'range') return null;
  return equalityOperator(ops);
}
